import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import DataTableComponentFrontPagination from "../../common/DataTableComponentFrontPagination";
import { OHS_BASE_PATH } from "./ohsConstants";
import { DaysLeftBadge, MutedText, SectionCard, SectionHeader, SectionTitle } from "./Ohs.styles";

// Items expiring within 2 weeks (or already expired) get the red badge.
const URGENT_EXPIRY_DAYS = 14;
const EXPIRY_WINDOW_OPTIONS = [30, 60, 90, 120, 180].map((days) => ({ value: String(days), label: `${days} days` }));
const EXPORT_HEADER = ["Item", "Box", "Campus", "Building", "Expiry Date", "Days Left"];

/**
 * Day-count wording for an expiry date relative to today.
 * @param {number} daysLeft Negative once expired.
 * @returns {string} e.g. "5 days left", "Expires today", "Expired 3 days ago"
 */
function describeDaysLeft(daysLeft) {
    if (daysLeft > 0) {
        return `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`;
    }
    if (daysLeft === 0) {
        return "Expires today";
    }
    return `Expired ${Math.abs(daysLeft)} ${daysLeft === -1 ? "day" : "days"} ago`;
}

/**
 * Items whose latest first aider check has them expiring within the chosen window, soonest
 * first, shown as a day count rather than a raw date.
 * @param {Object} props
 * @param {Array<Object>} props.expiringSoon useFirstAidAnalytics().expiringSoon
 * @param {Object} props.expiryFilter Tablefilter state ({ arr, checkObj }) for the single-select window.
 * @param {Function} props.setExpiryFilter Tablefilter setState.
 */
function ExpiringSoon({ expiringSoon, expiryFilter, setExpiryFilter }) {
    const [search, setSearch] = useState("");

    const expiringRows = useMemo(() => {
        const searchText = search.trim().toLowerCase();
        return expiringSoon
            .map((expiringItem) => ({ ...expiringItem, rowKey: `${expiringItem.box_id}-${expiringItem.box_item_id}` }))
            .filter((expiringItem) => {
                return !searchText
                    || expiringItem.item_name.toLowerCase().includes(searchText)
                    || expiringItem.box_code.toLowerCase().includes(searchText)
                    || (expiringItem.campus_name || "").toLowerCase().includes(searchText);
            });
    }, [expiringSoon, search]);

    const columns = useMemo(
        () => [
            {
                name: "Item",
                selector: "item_name",
                sortable: true,
                grow: 2,
                minWidth: "220px",
            },
            {
                name: "Box",
                selector: "box_code",
                sortable: true,
                minWidth: "200px",
                cell: (row) => (
                    <div>
                        <Link to={`${OHS_BASE_PATH}/boxes/open/${row.box_id}`}>{row.box_code}</Link>
                        <div><MutedText>{row.campus_name} · {row.building_name}</MutedText></div>
                    </div>
                ),
            },
            {
                name: "Expiry",
                selector: "daysLeft",
                sortable: true,
                minWidth: "170px",
                cell: (row) => (
                    <DaysLeftBadge $isUrgent={row.daysLeft <= URGENT_EXPIRY_DAYS} title={moment(row.expiry_date).format("D MMMM YYYY")}>
                        {describeDaysLeft(row.daysLeft)}
                    </DaysLeftBadge>
                ),
            },
        ],
        []
    );

    /**
     * Rows for the CSV/XLSX/PDF export.
     * @returns {Object} { header, data, pdfData }
     */
    const exportData = () => {
        const exportRows = expiringRows.map((expiringItem) => [
            expiringItem.item_name,
            expiringItem.box_code,
            expiringItem.campus_name,
            expiringItem.building_name,
            expiringItem.expiry_date,
            expiringItem.daysLeft,
        ]);

        return {
            header: EXPORT_HEADER,
            data: exportRows.map((exportRow) => Object.fromEntries(EXPORT_HEADER.map((columnName, columnIndex) => [columnName, exportRow[columnIndex]]))),
            pdfData: exportRows,
        };
    };

    return (
        <SectionCard>
            <SectionHeader>
                <div>
                    <SectionTitle>Expiring Soon</SectionTitle>
                    <MutedText>Already-expired items are included until a check records a new date.</MutedText>
                </div>
            </SectionHeader>
            <DataTableComponentFrontPagination
                data={expiringRows}
                columns={columns}
                keyField="rowKey"
                search={search}
                setSearch={setSearch}
                exportData={exportData}
                exportFileName="First_Aid_Expiring_Soon"
                defaultSort={{ defaultSortColumn: "daysLeft", defaultSortAsc: true }}
                filters={[
                    {
                        filterName: "Expiring Within",
                        optionArr: EXPIRY_WINDOW_OPTIONS,
                        state: expiryFilter,
                        setState: setExpiryFilter,
                        uniqueId: "ohsExpiry",
                        singleSelect: true,
                        stopOptionSorting: true,
                    },
                ]}
            />
        </SectionCard>
    );
}

export default ExpiringSoon;
